import React from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import Menubar, { MenubarContainer } from '~/components/Menubar'
import { useViewer } from '~/components/ViewerContext'
import { H2, device, space } from '@ticketswap/solar'
import { NarrowPageLayout } from '~/components/PageLayout'
import ListingFaq from '~/components/ListingFaq'
import { Block, BlockHeader } from '~/components/Block'
import Available from '~/components/EventListings/Available'
import Purchaser from '~/components/Purchaser'
import { listingListFragmentPropTypes } from '~/graphql/fragments/listingList'
import ListingSecureSwap from '~/components/ListingSecureSwap'
import SellerInformation from '~/components/SellerInformation'
import ListingHeader from '~/components/ListingHeader'

const StyledBlock = styled(Block)`
  @media ${device.tablet} {
    margin-bottom: 40px;
  }
`

const ListingBlocks = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: ${space[8]};

  @media ${device.tablet} {
    grid-template-columns: repeat(${props => (props.hasSecureSwap ? 2 : 1)}, 1fr);
    grid-gap: ${space[16]};
  }
`

const ListingPageContent = ({ t, data, randomCover, id, hash }) => {
  const { deviceInfo } = useViewer()
  const { isMobile } = deviceInfo
  const { viewer = {}, listing = {} } = data
  const { event = {}, eventType = {} } = listing
  const { headerImageUrl, location, imageUrl } = event
  const coverImage = isMobile ? imageUrl : headerImageUrl || location?.background || randomCover
  let ticketsFromListingInUsersCart = []
  if (viewer.cart && viewer.cart.tickets) {
    for (let i = 0; i < listing.tickets.edges.length; i++) {
      const ticketInListing = listing.tickets.edges[i].node
      for (let j = 0; j < viewer.cart.tickets.length; j++) {
        const ticketInCart = viewer.cart.tickets[j]
        if (ticketInListing.id === ticketInCart.id) {
          ticketsFromListingInUsersCart = [...ticketsFromListingInUsersCart, ticketInCart.id]
        }
      }
    }
  }
  const eventIsExpired =
    event.__typename === 'ExpiredEvent' || event.__typename === 'CancelledEvent'
  const eventTypeIsExpired = eventType && eventType.isExpired
  const listingIsSold = listing.status === 'SOLD'
  const listingIsDeleted = listing.status === 'DELETED'
  const listingIsReserved =
    ticketsFromListingInUsersCart.length === 0 &&
    listing.numberOfTicketsStillForSale > 0 &&
    listing.tickets &&
    listing.tickets.edges &&
    listing.tickets.edges.filter(({ node }) => node.status === 'RESERVED').length ===
      listing.numberOfTicketsStillForSale
  const listingHasAvailableTickets = [
    eventIsExpired,
    eventTypeIsExpired,
    listingIsSold,
    listingIsDeleted,
    listingIsReserved,
  ].every(item => !item)
  const status = {
    eventIsExpired,
    eventTypeIsExpired,
    listingIsSold,
    listingIsDeleted,
    listingIsReserved,
    listingHasAvailableTickets,
  }
  const showOtherAvailableListings =
    !listingHasAvailableTickets && eventType && eventType.availableTicketsCount > 0
  const isBuyingBlocked = Boolean(event?.isBuyingBlocked)

  return (
    <>
      <MenubarContainer>
        <Menubar appearance="light" />
      </MenubarContainer>

      <ListingHeader
        t={t}
        event={event}
        eventType={eventType}
        coverImage={coverImage}
        listing={listing}
      />

      <NarrowPageLayout>
        <StyledBlock>
          <Purchaser
            t={t}
            status={status}
            listing={{ id, hash, ...listing }}
            ticketsFromListingInUsersCart={ticketsFromListingInUsersCart}
            isBuyingBlocked={isBuyingBlocked}
          />

          <ListingBlocks hasSecureSwap={listing.isSecureSwapAvailable}>
            <SellerInformation t={t} {...listing} />
            <ListingSecureSwap t={t} listing={listing} />
          </ListingBlocks>
        </StyledBlock>

        {showOtherAvailableListings && (
          <StyledBlock>
            <BlockHeader>
              <H2>{t('listing:listings.available')}</H2>
            </BlockHeader>
            <Available t={t} eventTypeId={eventType && eventType.id} />
          </StyledBlock>
        )}

        <ListingFaq t={t} showSecureSwapFaq={listing && listing.isSecureSwapAvailable} />
      </NarrowPageLayout>
    </>
  )
}

ListingPageContent.propTypes = {
  t: PropTypes.func.isRequired,
  data: PropTypes.shape({
    viewer: PropTypes.shape({
      id: PropTypes.string.isRequired,
      cart: PropTypes.shape({
        tickets: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.string,
          })
        ),
      }),
    }),
    listing: listingListFragmentPropTypes,
  }),
  randomCover: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  hash: PropTypes.string.isRequired,
}

export default ListingPageContent
